import { useCallback, useMemo } from 'react';

import {
  getLongName as computeLongName,
  getShortName as computeShortName,
} from '@/utils/integrationDisplay.util';
import { normalizeOriginName } from '@/utils/statisticValidation.util';

import { useGetCapabilities } from './api/useGetCapabilities';

/**
 * Centralized hook for integration display names.
 *
 * This is the SINGLE SOURCE OF TRUTH for integration display logic across
 * the entire application. All integration name rendering should use this hook
 * to ensure consistency between filters, tables, and other UI components.
 *
 * **Why this exists:**
 * Previously, three different systems existed for getting integration display names:
 * 1. useGetVulnerabilityFilters (backend titles) - Used by vulnerability table
 * 2. buildSourceOptions (hardcoded AllIntegrations.name) - Used by asset Origin filter
 * 3. getDisplayOriginText (hybrid approach) - Used by Origin column
 *
 * This caused UI inconsistencies where filters showed "Burp Enterprise" but
 * columns showed "Burp". This hook consolidates all logic to use backend `title`
 * field as primary source with proper fallback chain.
 *
 * @returns Object with display utility functions and loading state
 *
 * @example
 * // Get display name for an integration
 * ```tsx
 * const { getDisplayName, isLoading } = useIntegrationDisplay();
 *
 * if (isLoading) return <Spinner />;
 *
 * const displayName = getDisplayName('burpsuite'); // Returns "Burp" from backend
 * ```
 *
 * @example
 * // Check if value is a valid integration
 * ```tsx
 * const { isIntegration } = useIntegrationDisplay();
 *
 * if (isIntegration('burpsuite')) {
 *   // This is a third-party integration
 * } else {
 *   // This is a Chariot native capability
 * }
 * ```
 *
 * @example
 * // Get all integrations with display names for dropdowns
 * ```tsx
 * const { getAllIntegrations, isLoading } = useIntegrationDisplay();
 *
 * const integrations = getAllIntegrations();
 * // Returns: [{ name: 'burpsuite', title: 'Burp', integration: true }, ...]
 * ```
 */
export function useIntegrationDisplay() {
  const { data, status, integrations: rawIntegrations } = useGetCapabilities();

  /**
   * Get display name for an integration using backend title.
   *
   * **Fallback chain:**
   * 1. Backend `integration.title` (primary source - e.g., "Burp")
   * 2. Normalized name transformation (e.g., "burpsuite" → "Burpsuite")
   *
   * @param integrationId - Raw integration identifier (e.g., 'burpsuite', 'microsoft-defender')
   * @returns Formatted display name for UI rendering
   */
  const getDisplayName = useMemo(
    () =>
      (integrationId: string): string => {
        if (!integrationId) return '';
        if (!data.allCapabilities || data.allCapabilities.length === 0) {
          return normalizeOriginName(integrationId);
        }

        const capability = data.allCapabilities.find(
          cap => cap.name.toLowerCase() === integrationId.toLowerCase()
        );

        return capability?.title || normalizeOriginName(integrationId);
      },
    [data.allCapabilities]
  );

  /**
   * Check if a string value is a valid integration ID.
   *
   * Integrations are third-party tools (Burp, Tenable, etc.) as opposed to
   * Chariot native capabilities.
   *
   * @param value - String to check (e.g., 'burpsuite', 'nuclei')
   * @returns True if value is a known integration, false otherwise
   */
  const isIntegration = useMemo(
    () =>
      (value: string): boolean => {
        if (!value || !rawIntegrations || rawIntegrations.length === 0) {
          return false;
        }

        return rawIntegrations.some(
          integration => integration.name.toLowerCase() === value.toLowerCase()
        );
      },
    [rawIntegrations]
  );

  /**
   * Get all integrations with their display names.
   *
   * Useful for building dropdown options or filter lists.
   *
   * @returns Array of integration capability objects with titles
   */
  const getAllIntegrations = useMemo(
    () => () => {
      return rawIntegrations || [];
    },
    [rawIntegrations]
  );

  /**
   * Get short display name for an integration.
   *
   * Used in space-constrained UI elements like table cells, filter dropdowns,
   * and compact card views. Applies manual overrides and algorithmic shortening.
   *
   * Priority:
   * 1. Manual override from SHORT_NAME_OVERRIDES (e.g., 'burpsuite' → 'Burp')
   * 2. Algorithmic shortening (removes verbose suffixes)
   * 3. Fallback to integration ID if capability not found
   *
   * @param integrationId - Integration identifier (e.g., 'burpsuite')
   * @returns Short display name suitable for compact spaces
   *
   * @example
   * const { getShortName } = useIntegrationDisplay();
   * getShortName('burpsuite') // Returns 'Burp'
   * getShortName('qualys') // Returns 'Qualys VM'
   */
  const getShortName = useCallback(
    (integrationId: string): string => {
      if (!integrationId) return '';
      if (!data.allCapabilities || data.allCapabilities.length === 0) {
        return integrationId;
      }

      const capability = data.allCapabilities.find(
        cap => cap.name.toLowerCase() === integrationId.toLowerCase()
      );

      if (!capability) return integrationId;

      return computeShortName(capability.title || '', capability.name);
    },
    [data.allCapabilities]
  );

  /**
   * Get long display name for an integration.
   *
   * Used in detail pages and places where full context is needed.
   * Returns the complete backend title without shortening.
   *
   * @param integrationId - Integration identifier (e.g., 'burpsuite')
   * @returns Long display name suitable for detail views
   *
   * @example
   * const { getLongName } = useIntegrationDisplay();
   * getLongName('burpsuite') // Returns 'Burp Suite Enterprise'
   */
  const getLongName = useCallback(
    (integrationId: string): string => {
      if (!integrationId) return '';
      if (!data.allCapabilities || data.allCapabilities.length === 0) {
        return integrationId;
      }

      const capability = data.allCapabilities.find(
        cap => cap.name.toLowerCase() === integrationId.toLowerCase()
      );

      if (!capability) return integrationId;

      return computeLongName(capability.title || '');
    },
    [data.allCapabilities]
  );

  return {
    /**
     * Get display name for integration ID
     * @example getDisplayName('burpsuite') // "Burp"
     */
    getDisplayName,

    /**
     * Check if value is a valid integration
     * @example isIntegration('burpsuite') // true
     */
    isIntegration,

    /**
     * Get all integrations with display names
     * @example getAllIntegrations() // [{ name: 'burpsuite', title: 'Burp', ... }]
     */
    getAllIntegrations,

    /**
     * Get short display name for integration (table cells, filters)
     * @example getShortName('burpsuite') // "Burp"
     */
    getShortName,

    /**
     * Get long display name for integration (detail pages)
     * @example getLongName('burpsuite') // "Burp Suite Enterprise"
     */
    getLongName,

    /**
     * Loading state - true while fetching capabilities from backend
     */
    isLoading: status === 'pending',

    /**
     * Raw capabilities data (all capabilities including integrations)
     */
    capabilities: data.allCapabilities,

    /**
     * Raw integrations only (filtered from capabilities)
     */
    integrations: rawIntegrations,
  };
}
